import * as M from "../movement.mjs";
import { createMatch, MAPS } from "../scenario.mjs";
import { EFFECTS } from "../movement.mjs";
import { playAiTurn } from "../ai.mjs";
import { advance, shouldShow, dismiss, reset } from "../coach.mjs";
import { PAGES, step as tutorialStep, seen as tutorialSeen, markSeen } from "../tutorial.mjs";
import { HexWorld } from "./world.js";
import * as UI from "./ui.js";
import * as SFX from "./audio.js";
import { Minimap } from "./minimap.js";
import * as Juice from "./juice.js";

// Card art: plain emoji glyphs (system fonts, no external assets).
const ICON = { infantry: "🪖", tank: "🛡️", harvester: "⛏️", artillery: "💥", helicopter: "🚁", jet: "✈️", gunboat: "🚤", destroyer: "🚢", battleship: "⚓",
  aa: "🎯", turret: "🗼", dock: "⚓", coastal: "🏰", research: "🧪" };
const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);
function deckCard({ attrs, on, ok, locked, icon, name, cost, ap, sub = "", tip }) {
  return `<button type="button" class="deck-card ${on ? "active" : ""} ${locked ? "locked" : ""}" ${ok ? "" : "disabled"} ${attrs} title="${tip}">
    <span class="dc-ap" aria-label="${ap} AP">${ap}</span>
    <span class="dc-icon" aria-hidden="true">${icon}</span>
    <span class="dc-name">${name}</span>
    ${sub ? `<span class="dc-sub">${sub}</span>` : ""}
    ${cost != null ? `<span class="dc-cost"><i class="ph-dot" aria-hidden="true"></i>${cost}</span>` : ""}
    ${locked ? `<span class="dc-lock">${locked}</span>` : ""}
  </button>`;
}

// Juice: diff hit points between paints so every damage source (you, AI, turrets) pops a number.
let lastHp = null, lastTurn = null;
function juiceDiff() {
  const now = new Map();
  for (const u of state.units) now.set("u" + u.id, { hp: u.hp ?? M.UNITS[u.type].hp, q: u.q, r: u.r, owner: u.owner });
  for (const s of state.structures ?? []) now.set("s" + s.id, { hp: s.hp, q: s.q, r: s.r, owner: s.owner });
  let hits = 0, kos = 0;
  if (lastHp) {
    for (const [id, was] of lastHp) {
      const cur = now.get(id);
      if (cur && cur.hp < was.hp) {
        const at = world.screenOf(cur);
        Juice.floatText(at.x, at.y, `-${was.hp - cur.hp}`, was.owner === 1 ? "hurt" : "dmg");
        hits++;
      } else if (!cur) {
        const at = world.screenOf(was);
        Juice.floatText(at.x, at.y, "KO!", "ko");
        kos++;
      }
    }
  }
  lastHp = now;
  if (kos) Juice.shake(2);
  else if (hits) Juice.shake(1);
  const t = `${state.turn.player}:${state.turn.round ?? 1}`;
  if (t !== lastTurn && M.winner(state) === null) {
    if (lastTurn !== null || state.turn.player === 1) Juice.banner(state.turn.player === 1 ? "YOUR TURN" : "ENEMY TURN", state.turn.player === 1 ? "you" : "foe");
  }
  lastTurn = t;
}

const canvas = document.getElementById("board");
const world = new HexWorld(canvas);

// Map choice: ?map=frontline&seed=7 in the URL wins, then the last pick, then Frontline #1.
const MAP_KEY = "iron-command-map";
function parseFx(raw) {
  return String(raw || "").split(",").map((s) => s.trim()).filter((id) => EFFECTS[id]);
}
function readChoice() {
  const p = new URLSearchParams(location.search);
  const map = p.get("map");
  const extras = parseFx(p.get("fx"));
  if (map && MAPS[map]) return { map, seed: Math.max(1, Number(p.get("seed")) || 1), effects: extras };
  try {
    const saved = JSON.parse(localStorage.getItem(MAP_KEY) || "null");
    if (saved && MAPS[saved.map]) return { map: saved.map, seed: Math.max(1, Number(saved.seed) || 1), effects: saved.effects ?? extras };
  } catch { /* private mode */ }
  return { map: "frontline", seed: 1, effects: extras };
}
let choice = readChoice();
let state = createMatch(choice);
const minimap = new Minimap(document.getElementById("minimap"), world);
let selected = new Set();
let mode = "none";
let path = [];
let hover = null;
let dirKind = null;
let wheelPivot = null;
let busy = false;
let coachStep = shouldShow() ? "select" : null;
// Build / reserve placement: { kind: "produce" | "structure", type } or { kind: "drop", index }. Engine validates the hex.
let placing = null;
let buildTab = "units";
// Hard lock-on: first tap on a target locks it (reticle + damage), second tap or Fire shoots.
let lock = null;
let tutorialPage = tutorialSeen() ? null : 0;

// Enemy unit, structure or base on a hex, as a planAttack target.
function targetAt(h) {
  const me = state.turn.player;
  const u = M.unitAt(state, h);
  if (u && u.owner !== me) return { target: { unitId: u.id }, air: M.isAir(u), type: u.type };
  const s = M.structureAt(state, h);
  if (s && s.owner !== me) return { target: { structureId: s.id }, air: false, type: s.type };
  const b = M.baseAt(state, h);
  if (b && b.owner !== me && (b.hp ?? 0) > 0) return { target: { base: b.owner }, air: false, type: "base" };
  return null;
}

function softLock() {
  if (mode !== "attack" || selected.size !== 1 || !hover) return null;
  const t = targetAt(hover);
  if (!t) return null;
  const plan = M.planAttack(state, [...selected][0], t.target);
  return plan.ok ? { q: hover.q, r: hover.r, air: t.air, type: t.type, plan } : null;
}

function view() {
  const id = selected.size === 1 ? [...selected][0] : null;
  const soft = lock ? null : softLock();
  return {
    lock: lock ? { ...lock, hard: true } : soft,
    selected,
    hover,
    path,
    threats: M.threatCells(state, state.turn.player),
    reach: mode === "move" && id ? M.reachableHexes(state, id) : [],
    attacks: mode === "attack" && id ? M.attackTargets(state, id) : [],
    drops: placing?.kind === "structure" ? M.buildHexes(state, state.turn.player, placing.type)
      : placing?.kind === "produce" ? M.dropHexes(state, state.turn.player, placing.type)
      : placing?.kind === "drop" ? M.dropHexes(state, state.turn.player, state.reserves[state.turn.player]?.[placing.index]?.type)
      : [],
  };
}

function syncWorld() {
  world.setView(state, view());
}

function noteCoach(event) {
  if (!coachStep) return;
  const next = advance(coachStep, event);
  if (next === coachStep) return;
  coachStep = next;
  if (!coachStep) dismiss();
  UI.renderCoach(coachStep);
}

const TERRAIN_NAME = {
  plains: "Plains", ore: "Ore vein", ruins: "Town ruins", mountain: "Mountain", plateau: "Plateau", pit: "Spike pit",
  forest: "Forest", hills: "Hills", water: "Water", ford: "Ford", road: "Road",
};
const LINK_TEXT = {
  stairs: `stairs, any unit, +${M.LINKS.stairs.extra} AP`,
  ladder: `ladder, infantry only, +${M.LINKS.ladder.extra} AP`,
  jump: `jump pad, infantry/tank, ${M.RULES.jumpAp} AP, ends move`,
};
const INCOME_NAME = { base: "Base", ore: "Ore held", harvester: "Harvesters" };
const fmt = (n) => n.toLocaleString("en-US");

function hexInfo(h) {
  const id = M.terrainId(state, h);
  const t = M.TERRAIN[id];
  const base = M.baseAt(state, h);
  const u = M.unitAt(state, h);
  const lines = [];
  if (base) lines.push(`<b>${state.players[base.owner].faction} command base</b> · HP ${base.hp}/${base.hpMax ?? M.RULES.baseHp} · ${M.RULES.baseCover}% cover`);
  else lines.push(`<b>${TERRAIN_NAME[id]}</b> <span class="coord">${h.q},${h.r}</span>`);
  if (!base) {
    if (id === "pit") lines.push("Impassable on foot · cross by jump pad");
    else if (id === "water") lines.push("Ships only · land units cannot enter");
    else lines.push(t.cost === null ? "Impassable · blocks line of sight" : `Move ${t.cost} AP${t.cover ? ` · ${t.cover}% cover` : ""}`);
  }
  if (M.levelOf(state, h) > 0) lines.push(`High ground · +${M.RULES.highGround}% firing down · cliffs block plain steps`);
  else if (t.vantage) lines.push(`Vantage · +${M.RULES.highGround}% firing down on flat ground`);
  if (t.blocksLOS && id !== "mountain") lines.push("Blocks line of sight");
  for (const l of state.links ?? []) {
    const other = M.eq(l.a, h) ? l.b : M.eq(l.b, h) ? l.a : null;
    if (other) lines.push(`↔ ${other.q},${other.r} by ${LINK_TEXT[l.kind]}`);
  }
  if (id === "ore") {
    const owner = state.oreOwner?.[M.key(h)];
    lines.push(owner
      ? `Claimed by ${state.players[owner].faction} · +${M.RULES.oreIncome} Ph/turn`
      : `Unclaimed · end a turn with infantry here for +${M.RULES.oreIncome} Ph/turn`);
  }
  if (u) lines.push(`${u.owner === 1 ? "GVS" : "BOE"} ${u.type}${M.isAir(u) ? " (airborne)" : M.isNaval(u) ? " (ship)" : ""} · HP ${u.hp}/${M.UNITS[u.type].hp}${u.fresh ? " · fresh" : ""}`);
  const st = M.structureAt(state, h);
  if (st) {
    const t = M.STRUCTURES[st.type];
    const max = M.structureHp(state, st.type);
    lines.push(`${st.owner === 1 ? "GVS" : "BOE"} ${structLabel(st.type)} · HP ${st.hp}/${max}${t.atk ? ` · range ${t.range[1]}` : ""}`);
  }
  if (state.effects?.length) {
    lines.push(`Effects: ${state.effects.map((id) => EFFECTS[id]?.name ?? id).join(", ")}`);
  }
  return `<div class="hex-info">${lines.join("<br>")}</div>`;
}

function structLabel(type) {
  return { aa: "AA battery", turret: "gun turret", dock: "dock", coastal: "coastal gun" }[type] ?? type;
}

function economyHtml() {
  const you = M.incomeBreakdown(state, 1);
  const foe = M.incomeBreakdown(state, 2);
  const parts = you.lines.map((l) => l.id === "base"
    ? `${INCOME_NAME.base} ${l.total}`
    : `${INCOME_NAME[l.id]} ${l.count}×${l.each}`).join(" · ");
  const fill = Math.min(100, Math.round(you.stock / 2000 * 100));
  return `
    <div class="ph-bar" role="meter" aria-valuemin="0" aria-valuemax="2000" aria-valuenow="${you.stock}" aria-label="Phytane"><span style="width:${fill}%"></span>
      <i class="ph-gem" aria-hidden="true"></i><b>${fmt(you.stock)}</b><em>+${fmt(you.total)}</em></div>
    <div class="eco-stock"><span class="ph-dot" aria-hidden="true"></span><b>${fmt(you.stock)}</b><span>Phytane</span></div>
    <div class="eco-next"><b>+${fmt(you.total)}</b> next turn <small>${parts}</small></div>
    <div class="eco-foe">BOE ${fmt(foe.stock)} <small>+${fmt(foe.total)}/turn</small></div>`;
}

function paint() {
  syncWorld();
  juiceDiff();
  document.body.classList.toggle("foe-turn", state.turn.player !== 1);
  const tb = document.getElementById("turn-pill");
  if (tb) {
    tb.className = `turn-pill ${state.turn.player === 1 ? "you" : "foe"}`;
    tb.innerHTML = `<b>${state.turn.player === 1 ? "YOUR TURN" : "ENEMY TURN"}</b><span>Round ${state.turn.round ?? 1}/${M.rule(state, "maxTurns")}</span>`;
  }
  const apEl = document.getElementById("ap-pips");
  if (apEl) {
    const max = M.rule(state, "apPerTurn");
    apEl.innerHTML = Array.from({ length: max }, (_, i) => `<i class="${i < state.turn.ap ? "on" : ""}"></i>`).join("") + `<b>${state.turn.ap} AP</b>`;
  }
  const p = state.turn.player;
  const pl = state.players[p];
  const mapName = choice.map === "skirmish" ? "Skirmish" : `${MAPS[choice.map]?.label ?? choice.map} #${choice.seed}`;
  const fxChips = (state.effects ?? []).map((id) => `<span class="fx-chip" title="${EFFECTS[id]?.description ?? ""}">${EFFECTS[id]?.name ?? id}</span>`).join("");
  UI.renderStatus(`
    <span class="map-name">${mapName}</span>
    <b>${pl.faction}</b>
    · <span class="tech">Tech T${M.techOf(state, 1)} vs T${M.techOf(state, 2)}</span>
    ${fxChips}
  `);
  UI.renderEconomy(economyHtml());

  let preview = "";
  const shown = lock ?? softLock();
  if (shown) {
    const plan = shown.plan;
    const mods = plan.mods.map(([m, v]) => `${m.replace(/_/g, " ")} ${v > 0 ? "+" : ""}${v}`).join(" · ");
    preview = `<div class="unit-card lock ${lock ? "hard" : ""}">${lock ? "LOCKED" : "Target"} · <b>${plan.damage}</b> dmg
      ${plan.angle ? ` · ${plan.angle}` : ""}<br><small>${mods || "no modifiers"}</small>
      ${lock ? "<br><small>Tap the target again, Fire, or Enter</small>" : ""}</div>`;
  } else if (mode === "attack" && selected.size === 1 && hover) {
    const t = targetAt(hover);
    const plan = t && M.planAttack(state, [...selected][0], t.target);
    if (plan && !plan.ok) preview = `<div class="unit-card">${plan.error.replace(/_/g, " ").toLowerCase()}</div>`;
  }

  const cards = [...selected].map((id) => {
    const u = M.byId(state, id);
    if (!u) return "";
    const w = M.wedgeAt(state, u);
    return `<div class="unit-card ${u.owner === 2 ? "boe" : ""} ${u.fresh ? "fresh" : ""}">
      <b>${u.id}</b> · ${u.type} · HP ${u.hp}<br>
      (${u.q},${u.r}) face ${u.facing}
      ${w ? `· <span class="wedge">WEDGE ${w.kind}</span>` : ""}
      ${u.fresh ? "· FRESH" : ""} ${u.attacked ? "· FIRED" : ""}
    </div>`;
  }).join("");
  const reachHint = mode === "move" && selected.size === 1
    ? `<i class="muted">Green hexes are legal steps. Tap the last hex again to commit.</i>`
    : `<i class="muted">Tap a friendly unit. Red rings are hexes the enemy can hit. Keys: M move · A attack · E end · R recenter</i>`;
  let place = "";
  if (placing) {
    const what = placing.kind === "drop" ? state.reserves[p][placing.index]?.type : placing.type;
    const where = placing.kind === "structure"
      ? (M.STRUCTURES[placing.type]?.coastal ? "a cyan coastal hex" : "a cyan hex in your territory")
      : M.UNITS[placing.type ?? state.reserves[p][placing.index]?.type]?.naval ? "cyan water next to a dock" : "a cyan hex next to your base";
    place = `<div class="unit-card place">Place <b>${what}</b>: tap ${where}. Esc cancels.</div>`;
  }
  const hoverInfo = hover && M.inBounds(state, hover) ? hexInfo(hover) : "";
  UI.renderInspect(place + preview + (cards || hoverInfo || (placing ? "" : reachHint)));

  UI.setModeName(mode, path.length ? ` · path ${path.length}` : "");
  const commit = document.getElementById("btn-commit");
  if (commit) {
    commit.hidden = !(lock || (mode === "move" && path.length && selected.size === 1));
    commit.textContent = lock ? "Fire" : "Commit move";
    commit.classList.toggle("fire", !!lock);
  }

  const canAct = !busy && M.winner(state) === null;
  UI.setBuildTab(buildTab);
  const tech = M.techOf(state, p);
  let html = "";
  if (buildTab === "units") {
    html = Object.keys(M.UNIT_COSTS).map((type) => {
      const plan = canAct ? M.planProduce(state, type) : { error: "BUSY" };
      const need = M.UNIT_TECH[type] ?? 1;
      const on = placing?.kind === "produce" && placing.type === type;
      return deckCard({ attrs: `data-prod="${type}"`, on, ok: plan.ok, locked: need > tech ? `T${need}` : "", icon: ICON[type] ?? "⬢",
        name: cap(type), cost: M.UNIT_COSTS[type], ap: M.UNIT_AP_COST[type], tip: plan.ok ? `Build ${type}: tap or drag onto a cyan hex` : plan.error });
    }).join("");
  } else if (buildTab === "defenses") {
    html = Object.entries(M.STRUCTURES).map(([type, t]) => {
      const can = canAct && t.tech <= tech && state.players[p].credits >= t.cost && state.turn.ap >= t.ap;
      const on = placing?.kind === "structure" && placing.type === type;
      const label = structLabel(type);
      const hits = type === "dock" ? "launch ships" : type === "coastal" ? "fires at ships" : t.vsAir ? "fires at aircraft" : "fires at ground and ships";
      return deckCard({ attrs: `data-struct="${type}"`, on, ok: can, locked: t.tech > tech ? `T${t.tech}` : "", icon: ICON[type] ?? "🏗️",
        name: cap(label), cost: t.cost, ap: t.ap, tip: `${label}: ${hits}` });
    }).join("");
  } else {
    const plan = canAct ? M.planResearch(state) : { error: "BUSY" };
    const next = tech + 1;
    const unlocks = { 2: "helicopters, destroyers, AA batteries", 3: "jets and battleships" }[next];
    html = next > M.RULES.maxTech
      ? `<span class="tray-empty">Top tier reached. Every tier you lead by adds ${M.RULES.techEdge}% damage.</span>`
      : deckCard({ attrs: `data-research="1"`, ok: plan.ok, icon: ICON.research, name: `Tier ${next}`, cost: M.RULES.researchCost[next],
          ap: M.RULES.researchAp, tip: plan.ok ? "Research" : plan.error }) + `
<span class="research-note">Unlocks ${unlocks}. Each tier ahead of the enemy: +${M.RULES.techEdge}% damage (behind: −${M.RULES.techEdge}%).</span>`;
  }
  UI.renderProduce(html);
  for (const btn of document.querySelectorAll("#produce [data-prod]")) {
    btn.onclick = () => startPlacing({ kind: "produce", type: btn.dataset.prod });
  }
  for (const btn of document.querySelectorAll("#produce [data-struct]")) {
    btn.onclick = () => startPlacing({ kind: "structure", type: btn.dataset.struct });
  }
  for (const btn of document.querySelectorAll("#produce [data-research]")) btn.onclick = () => research();

  const bag = state.reserves[1] || [];
  UI.renderReserves(bag.length
    ? bag.map((r, i) => {
      const plan = canAct && p === 1 ? M.planDrop(state, i) : { error: "BUSY" };
      const on = placing?.kind === "drop" && placing.index === i;
      return deckCard({ attrs: `data-drop="${i}"`, on, ok: plan.ok, icon: ICON[r.type] ?? "⬢", name: cap(r.type),
        sub: `captured`, ap: M.RULES.dropAp, tip: plan.ok ? `Drop ${r.type}: tap or drag beside your base` : plan.error }).replace("deck-card", "deck-card reserve");
    }).join("")
    : `<span class="tray-empty">Empty. Destroyed enemy units land here; drop one beside your base for ${M.RULES.dropAp} AP.</span>`);
  for (const btn of document.querySelectorAll("#reserves [data-drop]")) {
    btn.onclick = () => startPlacing({ kind: "drop", index: Number(btn.dataset.drop) });
  }

  UI.renderCoach(coachStep);
  minimap.draw(selected);
  maybeVictory();
}

function maybeVictory() {
  const w = M.winner(state);
  if (w === null) return;
  busy = true;
  UI.setBusy(true);
  SFX.win();
  const title = w === 0 ? "Draw" : `${state.players[w].faction} holds the field`;
  const why = state.timeExpired ? "Round limit — score decides." : "Enemy command base destroyed.";
  UI.showVictory(title, `${why}  GVS ${M.scoreOf(state, 1)} · BOE ${M.scoreOf(state, 2)}`);
}

function applyPlan(plan, label, keep = true) {
  if (!plan?.ok) {
    UI.log("✗ " + (plan?.error || "invalid") + (plan?.at ? ` @${plan.at.q},${plan.at.r}` : ""));
    return false;
  }
  if (plan.cost > state.turn.ap) { UI.log("✗ INSUFFICIENT_AP"); return false; }
  M.apply(state, plan);
  SFX.move();
  UI.log(`✓ ${label} · −${plan.cost} AP`);
  if (!keep) selected.clear();
  path = [];
  mode = "none";
  paint();
  return true;
}

function startPlacing(next) {
  if (busy) return;
  const same = placing && placing.kind === next.kind && placing.type === next.type && placing.index === next.index;
  placing = same ? null : next;
  mode = "none";
  path = [];
  lock = null;
  selected.clear();
  UI.hideDir();
  UI.hideWheel();
  if (placing) UI.log(placing.kind === "structure" ? "Tap a cyan hex in your territory" : "Tap a cyan hex next to your base");
  SFX.ui();
  paint();
}

function place(h) {
  if (placing.kind === "structure") {
    const plan = M.planBuild(state, placing.type, h);
    if (!plan.ok) return UI.log(plan.error === "BAD_BUILD_HEX" ? "Pick a cyan hex in your territory" : "✗ " + plan.error);
    const s = M.applyBuild(state, plan);
    SFX.ui();
    UI.log(`Built ${structLabel(s.type)} at ${s.q},${s.r} · −${plan.cost} AP`);
    placing = null;
    paint();
    return;
  }
  const isProduce = placing.kind === "produce";
  const plan = isProduce ? M.planProduce(state, placing.type, h) : M.planDrop(state, placing.index, h);
  if (!plan.ok) {
    UI.log(plan.error === "BAD_DROP_HEX" ? "Pick a cyan hex next to your base" : "✗ " + plan.error);
    return;
  }
  const u = isProduce ? M.applyProduce(state, plan) : M.applyDrop(state, plan);
  SFX.ui();
  UI.log(`${isProduce ? "Built" : "Dropped"} ${u.type} at ${u.q},${u.r} · −${plan.cost} AP`);
  placing = null;
  paint();
}

function research() {
  if (busy) return;
  const plan = M.planResearch(state);
  if (!plan.ok) return UI.log("✗ " + plan.error);
  M.applyResearch(state, plan);
  SFX.win();
  UI.log(`Research complete · tier ${plan.tech}`);
  paint();
}

// Shot effects from attack descriptors (player, AI or turret volley).
function fx(list) {
  const shots = list.filter((a) => a.from && a.to).map((a) => ({
    from: a.from, to: a.to, weapon: a.weapon, fromAir: !!M.UNITS[a.weapon]?.air, toAir: !!a.toAir, toType: a.toType,
    destroyed: a.destroyed, targetId: a.targetId,
  }));
  world.fire(shots);
  for (const a of shots) SFX.weapon(a.weapon);
  if (shots.some((a) => a.destroyed)) SFX.boom();
}

function volleyFx(volleys) {
  if (!volleys?.length) return;
  fx(volleys.map((v) => ({ from: v.fromHex, to: v.to, weapon: v.type, toAir: v.type === "aa", destroyed: v.destroyed, targetId: v.target })));
  for (const v of volleys) UI.log(`${v.type === "aa" ? "AA" : "Turret"} ${v.from} hits ${v.target} for ${v.damage}${v.destroyed ? " · down" : ""}`);
  SFX.hit();
}

function fireLock() {
  if (!lock || selected.size !== 1) return;
  const atk = M.byId(state, [...selected][0]);
  const plan = M.planAttack(state, atk.id, lock.plan.target);
  if (!plan.ok) { UI.log("✗ " + plan.error); lock = null; paint(); return; }
  const from = { q: atk.q, r: atk.r }, to = { q: lock.q, r: lock.r };
  const res = M.applyAttack(state, plan);
  fx([{ from, to, weapon: atk.type, toAir: lock.air, toType: lock.type, destroyed: res.destroyed, targetId: lock.plan.target.unitId }]);
  UI.log(`Attack ${plan.damage} dmg` + (plan.angle ? ` · ${plan.angle}` : "") + (res.destroyed ? " · destroyed" : ""));
  lock = null;
  selected.clear();
  mode = "none";
  noteCoach("attacked");
  paint();
}

function commitMove() {
  if (lock) { fireLock(); return; }
  if (mode === "move" && path.length && selected.size === 1) {
    if (applyPlan(M.planMove(state, [...selected][0], path), "MOVE")) noteCoach("moved");
  }
}

function setMode(m) {
  if (busy) return;
  if ((m === "move" || m === "attack" || m === "face") && !selected.size) {
    UI.log("Select units first");
    return;
  }
  mode = m;
  path = [];
  placing = null;
  lock = null;
  UI.hideDir();
  UI.hideWheel();
  if (m === "face") {
    dirKind = "face";
    UI.showDir("Face direction");
  }
  if (m === "move") noteCoach("mode-move");
  if (m === "attack") noteCoach("attack-mode");
  UI.log(m.toUpperCase() + " mode");
  SFX.ui();
  paint();
}

function pickDir(dir) {
  UI.hideDir();
  if (dirKind === "face") {
    applyPlan(M.planFace(state, [...selected], dir), "FACE");
  } else if (dirKind === "group") {
    applyPlan(M.planGroupMove(state, [...selected], dir), "GROUP MOVE", false);
  }
  dirKind = null;
}

function cancelOverlay() {
  dirKind = null;
  if (mode === "face") mode = "none";
  paint();
}

function groupMove() {
  if (busy) return;
  if (selected.size < 2) return UI.log("Select 2–5 units");
  dirKind = "group";
  UI.showDir("Group move direction");
}

function wheel() {
  if (busy) return;
  if (selected.size < 2) return UI.log("Select 2–5 units");
  wheelPivot = [...selected][0];
  UI.showWheel([...selected], (id) => { wheelPivot = id; UI.log("Pivot " + id); });
}

function confirmWheel(rot) {
  UI.hideWheel();
  applyPlan(M.planWheel(state, [...selected], wheelPivot, rot), "WHEEL", false);
}

function endTurn() {
  if (busy) return;
  const r = M.endTurn(state);
  SFX.ui();
  UI.log(`End turn → P${r.player} +${r.income} Ph`);
  volleyFx(r.volleys);
  selected.clear();
  mode = "none";
  path = [];
  placing = null;
  lock = null;
  noteCoach("ended");
  paint();
  if (M.winner(state) !== null) return;
  if (r.player === 2) runAi();
}

function runAi() {
  busy = true;
  UI.setBusy(true);
  UI.log("BOE is moving…");
  setTimeout(() => {
    const actions = playAiTurn(state);
    const shots = actions.filter((a) => a.kind === "attack");
    fx(shots);
    if (shots.length) SFX.hit();
    for (const a of actions) {
      if (a.kind === "attack") UI.log(`AI ${a.weapon ?? "unit"} hits for ${a.damage}` + (a.destroyed ? " · down" : ""));
      else if (a.kind === "move") UI.log(`AI move ${a.id}`);
      else if (a.kind === "group") UI.log("AI coordinated advance");
      else if (a.kind === "produce") UI.log(`AI built ${a.type}`);
      else if (a.kind === "build") UI.log(`AI built ${structLabel(a.type)} at ${a.to.q},${a.to.r}`);
      else if (a.kind === "research") UI.log(`AI researched tier ${a.tech}`);
      else if (a.kind === "end") {
        UI.log(`AI ends · you receive ${a.income} Ph`);
        setTimeout(() => volleyFx(a.volleys), shots.length * 260 + 400);
      }
    }
    busy = false;
    UI.setBusy(false);
    paint();
  }, 420);
}

function newMatch(next = choice) {
  lastHp = null; lastTurn = null;
  choice = next;
  try { localStorage.setItem(MAP_KEY, JSON.stringify(choice)); } catch { /* private mode */ }
  const url = new URL(location.href);
  url.searchParams.set("map", choice.map);
  if (choice.map !== "skirmish") url.searchParams.set("seed", String(choice.seed)); else url.searchParams.delete("seed");
  if (choice.effects?.length) url.searchParams.set("fx", choice.effects.join(",")); else url.searchParams.delete("fx");
  history.replaceState(null, "", url);
  state = createMatch(choice);
  UI.hidePicker();
  selected = new Set();
  mode = "none";
  path = [];
  hover = null;
  placing = null;
  busy = false;
  if (shouldShow()) coachStep = "select";
  UI.hideVictory();
  UI.hideDir();
  UI.hideWheel();
  UI.setBusy(false);
  world.attachBoard(state);
  overview = false;
  UI.setMinimap(world.large, window.innerWidth >= 720);
  minimap.setBoard(state);
  syncInsets();
  UI.log(`New match · ${MAPS[choice.map]?.label ?? choice.map}${choice.map !== "skirmish" ? ` #${choice.seed}` : ""} · destroy the red HQ or outscore by round ${M.rule(state, "maxTurns")}`);
  paint();
}

function skipCoach() {
  coachStep = null;
  dismiss();
  UI.renderCoach(null);
}

function showCoach() {
  reset();
  coachStep = "select";
  UI.renderCoach(coachStep);
}

function renderTutorial() {
  UI.renderTutorial(tutorialPage === null ? null : PAGES[tutorialPage], tutorialPage, PAGES.length);
}

function tutorial(action) {
  const wasOpen = tutorialPage !== null;
  tutorialPage = action === "open" ? 0 : tutorialStep(tutorialPage ?? 0, action);
  if (wasOpen && tutorialPage === null) {
    markSeen();
    showCoach();
  }
  renderTutorial();
}

// Recenter toggles between the home front and the whole board on large maps.
let overview = false;
function recenter() {
  overview = world.large && !overview;
  if (overview) world.frameOverview();
  else world.frameHome(state);
  UI.log(overview ? "Overview · R again for your front" : "Camera on your front");
  minimap.draw(selected);
}

function onHex(h) {
  if (busy || M.winner(state) !== null) return;
  const u = M.unitAt(state, h);
  const base = M.baseAt(state, h);

  if (placing) {
    place(h);
    return;
  }

  if (mode === "move" && selected.size === 1) {
    if (path.length && M.eq(path.at(-1), h)) {
      commitMove();
      return;
    }
    path.push(h);
    const plan = M.planMove(state, [...selected][0], path);
    if (!plan.ok) { UI.log("invalid: " + plan.error); path.pop(); }
    else UI.log(`path ${plan.cost} AP · tap last hex again, Commit, or Enter`);
    paint();
    return;
  }

  if (mode === "attack" && selected.size === 1) {
    const t = targetAt(h);
    if (!t) return;
    if (lock && lock.q === h.q && lock.r === h.r) { fireLock(); return; }
    const plan = M.planAttack(state, [...selected][0], t.target);
    if (!plan.ok) return UI.log("✗ " + plan.error);
    lock = { q: h.q, r: h.r, air: t.air, type: t.type, plan };
    SFX.ui();
    UI.log(`Locked on · ${plan.damage} dmg · tap again or Fire`);
    paint();
    return;
  }

  if (u && u.owner === state.turn.player) {
    if (selected.has(u.id)) selected.delete(u.id);
    else selected.add(u.id);
    if (selected.size) noteCoach("select");
    paint();
    return;
  }
  // Touch has no hover: tapping any other hex inspects it.
  hover = h;
  paint();
}

function openPicker() {
  UI.showPicker(choice, MAPS, EFFECTS);
}

UI.bindUi({
  setMode, groupMove, wheel, confirmWheel, endTurn, newMatch, pickDir, commitMove,
  cancelOverlay, recenter, skipCoach, tutorial, openPicker,
  setBuildTab: (tab) => { buildTab = tab; placing = null; paint(); },
  toggleMinimap: () => { UI.toggleMinimap(); minimap.setBoard(state); },
});

// Drag a deck card onto the board (Clash-style). A plain tap still works through onclick.
let drag = null;
function cardAction(btn) {
  if (btn.dataset.prod) return { kind: "produce", type: btn.dataset.prod };
  if (btn.dataset.struct) return { kind: "structure", type: btn.dataset.struct };
  if (btn.dataset.drop) return { kind: "drop", index: Number(btn.dataset.drop) };
  return null;
}
document.addEventListener("pointerdown", (e) => {
  const btn = e.target.closest(".deck-card");
  if (!btn || btn.disabled || busy) return;
  const next = cardAction(btn);
  if (next) drag = { next, x: e.clientX, y: e.clientY, btn, ghost: null, id: e.pointerId };
});
document.addEventListener("pointermove", (e) => {
  if (!drag || e.pointerId !== drag.id) return;
  const far = (e.clientX - drag.x) ** 2 + (e.clientY - drag.y) ** 2 > 196;
  if (!drag.ghost && far) {
    drag.ghost = drag.btn.cloneNode(true);
    drag.ghost.className += " drag-ghost";
    document.body.appendChild(drag.ghost);
    const same = placing && placing.kind === drag.next.kind && placing.type === drag.next.type && placing.index === drag.next.index;
    if (!same) startPlacing(drag.next);
  }
  if (drag.ghost) {
    drag.ghost.style.left = `${e.clientX}px`;
    drag.ghost.style.top = `${e.clientY}px`;
    const h = world.pick(e.clientX, e.clientY);
    if (h && !(hover && M.eq(hover, h))) { hover = h; paint(); }
  }
});
function endDrag(e) {
  if (!drag || e.pointerId !== drag.id) return;
  const d = drag;
  drag = null;
  if (!d.ghost) return;
  d.ghost.remove();
  d.btn.addEventListener("click", (ev) => { ev.stopImmediatePropagation(); ev.preventDefault(); }, { capture: true, once: true });
  const el = document.elementFromPoint(e.clientX, e.clientY);
  const h = el === canvas ? world.pick(e.clientX, e.clientY) : null;
  if (h && placing) onHex(h);
}
document.addEventListener("pointerup", endDrag);
document.addEventListener("pointercancel", (e) => { if (drag?.ghost) drag.ghost.remove(); drag = null; });

let ptr = null;
canvas.addEventListener("pointerdown", (e) => {
  ptr = { x: e.clientX, y: e.clientY };
  SFX.resume();
});
canvas.addEventListener("pointerup", (e) => {
  if (!ptr) return;
  const dx = e.clientX - ptr.x, dy = e.clientY - ptr.y;
  ptr = null;
  if (dx * dx + dy * dy > 25) return;
  const h = world.pick(e.clientX, e.clientY);
  if (h) onHex(h);
});
canvas.addEventListener("pointermove", (e) => {
  if (window.matchMedia("(pointer: coarse)").matches) return;
  const h = world.pick(e.clientX, e.clientY);
  if (!h || (hover && hover.q === h.q && hover.r === h.r)) return;
  hover = h;
  paint();
});

document.addEventListener("keydown", (e) => {
  if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
  const key = e.key;
  if (tutorialPage !== null) {
    if (key === "Escape") tutorial("skip");
    else if (key === "ArrowRight") tutorial("next");
    else if (key === "ArrowLeft") tutorial("back");
    return;
  }
  if (key === "Escape") {
    lock = null;
    placing = null;
    path = [];
    mode = "none";
    selected.clear();
    dirKind = null;
    UI.hideDir();
    UI.hideWheel();
    paint();
    return;
  }
  const pan = { ArrowUp: [0, 1], ArrowDown: [0, -1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] }[key];
  if (pan && !UI.pickerOpen()) {
    e.preventDefault();
    world.panView(pan[0], pan[1]);
    return;
  }
  if (UI.dirOpen() && key >= "0" && key <= "5") {
    pickDir(Number(key));
    return;
  }
  if (busy) return;
  if (key === "Enter" && lock) {
    fireLock();
    return;
  }
  if (key === "Enter" && mode === "move" && path.length && selected.size === 1) {
    commitMove();
    return;
  }
  const k = key.toLowerCase();
  if (k === "m") setMode("move");
  else if (k === "a") setMode("attack");
  else if (k === "f") setMode("face");
  else if (k === "g") groupMove();
  else if (k === "e") endTurn();
  else if (k === "c") commitMove();
  else if (k === "r") recenter();
  else if (k === "?" || k === "h") tutorial("open");
});

function syncInsets() {
  const top = document.querySelector(".topbar")?.getBoundingClientRect().bottom ?? 0;
  const dock = document.querySelector(".dock")?.getBoundingClientRect();
  world.setInsets(Math.round(top), Math.round(dock ? window.innerHeight - dock.top : 0));
}

window.addEventListener("resize", () => { world.resize(); syncInsets(); });
if ("ResizeObserver" in window) new ResizeObserver(syncInsets).observe(document.querySelector(".dock"));

function loop(t) {
  world.render(t);
  requestAnimationFrame(loop);
}
requestAnimationFrame(loop);

  UI.toggleLegend(window.innerWidth >= 720 && choice.map === "skirmish");
Juice.bindBounce();
newMatch();
renderTutorial();

// Dev-server only hook for screenshots and manual poking; stripped from production builds.
if (import.meta.env.DEV) window.__ironCommand = { get state() { return state; }, paint, onHex, M, world };
